/**
 * One team challenge (P3.5): join / leave, the caller's own progress, the team
 * aggregate (when released) and the opt-in leaderboard. The leaderboard toggle
 * is the participant's own choice and is off by default.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useArchiveChallengeMutation,
  useGetChallengeQuery,
  useJoinChallengeMutation,
  useLeaveChallengeMutation,
  useSetChallengeSharingMutation,
} from '../store/api/challengesApi';
import { useGetCapabilitiesQuery } from '../store/api/capabilitiesApi';
import { ChallengeLeaderboard } from '../components/challenges/ChallengeLeaderboard';
import { ChallengeProgressPanel } from '../components/challenges/ChallengeProgressPanel';
import { conditionUnit, describeCondition, MODE_LABEL } from '../components/challenges/challengeTypes';
import { LoadErrorNotice } from '../components/LoadErrorNotice';

/**
 * Why the challenge could not be shown. Only 404/400/422 mean "not found";
 * a 429 is the server asking us to wait (Retry-After honoured: Retry stays
 * disabled until it has elapsed), and anything else is a failure to load.
 */
function ChallengeLoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <LoadErrorNotice
      error={error}
      onRetry={onRetry}
      failedMessage="This challenge could not be loaded."
      notFound={<p className="text-gray-500" data-testid="challenge-not-found">Challenge not found.</p>}
      testIdPrefix="challenge"
    />
  );
}

export function ChallengeDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { data: serverCaps } = useGetCapabilitiesQuery();
  const enabled = serverCaps?.teamChallenges === true;
  const { data: challenge, isLoading, isError, error: loadError, refetch } = useGetChallengeQuery(id, { skip: !enabled || !id });
  const [join, { isLoading: joining }] = useJoinChallengeMutation();
  const [leave, { isLoading: leaving }] = useLeaveChallengeMutation();
  const [setSharing] = useSetChallengeSharingMutation();
  const [archive] = useArchiveChallengeMutation();
  const [shareOnJoin, setShareOnJoin] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (p: Promise<unknown>) => {
    setError(null);
    try {
      await p;
    } catch (e) {
      setError((e as { data?: { message?: string } })?.data?.message ?? 'Something went wrong. Please try again.');
    }
  };

  if (!enabled) return <p className="text-gray-500">Team challenges are not available yet.</p>;
  if (isLoading) return <p className="text-gray-500">Loading…</p>;
  if (isError) return <ChallengeLoadError error={loadError} onRetry={() => void refetch()} />;
  if (!challenge) return <p className="text-gray-500" data-testid="challenge-not-found">Challenge not found.</p>;

  const open = challenge.status === 'upcoming' || challenge.status === 'active';
  const unit = challenge.condition ? conditionUnit(challenge.condition) : '';

  return (
    <div className="space-y-6">
      <Link to="/challenges" className="text-sm text-primary-600 hover:underline">
        ← All challenges
      </Link>
      <div className="card p-6 space-y-2">
        <h1 className="text-2xl font-bold text-gray-900">{challenge.title}</h1>
        {challenge.description && <p className="text-gray-600">{challenge.description}</p>}
        <p className="text-sm">{describeCondition(challenge.condition)}</p>
        <p className="text-xs text-gray-500">
          {new Date(challenge.startsAt).toLocaleDateString()} – {new Date(challenge.endsAt).toLocaleDateString()} ·{' '}
          {MODE_LABEL[challenge.mode] ?? 'Personal goal'} · {challenge.aggregate.participants} joined
          {challenge.status === 'ended' && ' · Ended'}
        </p>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          {!challenge.me.joined && open && (
            <>
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  id="challenge-share-on-join"
                  checked={shareOnJoin}
                  onChange={(e) => setShareOnJoin(e.target.checked)}
                />
                Show me on this challenge’s leaderboard
              </label>
              <button type="button" className="btn btn-primary" disabled={joining} onClick={() => run(join({ id, shareOnLeaderboard: shareOnJoin }).unwrap())}>
                Join
              </button>
            </>
          )}
          {challenge.me.joined && (
            <>
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  id="challenge-share"
                  checked={challenge.me.shareOnLeaderboard}
                  onChange={(e) => run(setSharing({ id, shareOnLeaderboard: e.target.checked }).unwrap())}
                />
                Show me on this challenge’s leaderboard
              </label>
              <button type="button" className="btn btn-secondary" disabled={leaving} onClick={() => run(leave(id).unwrap())}>
                Leave
              </button>
            </>
          )}
          {challenge.canManage && challenge.status !== 'archived' && (
            <button type="button" className="btn btn-secondary" onClick={() => run(archive(id).unwrap())}>
              Archive
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500">
          You appear on the leaderboard only if you turn this on and your public profile shows scores. Otherwise only
          you see your progress.
        </p>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      <div className="card p-6">
        <ChallengeProgressPanel challenge={challenge} />
      </div>

      <div className="card p-6">
        <h2 className="font-semibold mb-3">{challenge.mode === 'competitive' ? 'Competitive board' : 'Leaderboard'}</h2>
        <ChallengeLeaderboard
          entries={challenge.leaderboard}
          unranked={challenge.unranked}
          unit={unit}
          target={challenge.condition?.target ?? null}
          competitive={challenge.mode === 'competitive'}
        />
      </div>
    </div>
  );
}

export default ChallengeDetailPage;
